/* eslint-disable @next/next/no-img-element */
import { useMobxStores } from '@/stores';
import { Affix, Button, Divider, Modal, Skeleton } from 'antd';
import { observer } from 'mobx-react-lite';
import FilterFields from './FilterFields';
import SearchList2 from '@/components2/SearchList';
import { LoadScriptNext } from '@react-google-maps/api';
import { googleMapsApiKey } from '@/utils/googleMaps';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap } from '@fortawesome/free-solid-svg-icons';
import NeedMoreData from './NeedMoreData';


const SearchMap = dynamic(() => import('@/components/Map'), { ssr: false });

function CardListC({
  configInfo,
  currentAirport,
  deviceType,
  checkin,
  checkout,
  props,
}) {
  const { searchStore } = useMobxStores();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const containerRef = useRef(null);
  const [bottomOffset, setBottomOffset] = useState(0);
  const [bgColor, setBgColor] = useState('bg-gray-100');

  const showModal = () => {
    searchStore.resetFacilities();
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        // Calculate the distance from the bottom of the container to the top of the viewport
        const containerBottom =
          containerRef.current.getBoundingClientRect().bottom;
        const viewportHeight = window.innerHeight;

        // Update 'bottomOffset'
        setBottomOffset(Math.max(0, viewportHeight - containerBottom));

        // change container color when scrolling finished and add 10 pixels to remove jumping effect
        if (containerBottom <= viewportHeight + 10) {
          setBgColor('bg-white');
        } else {
          setBgColor('bg-gray-100');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Skeleton
      paragraph={{ rows: 4 }}
      title
      loading={searchStore.isLoading || !searchStore.searchResult.result}
      className="pb-8"
    >
      <div className="max-w-[1600px] mx-auto px-2 lg:px-4 pb-2 lg:pb-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_550px] 2xl:grid-cols-[minmax(0,1fr)_700px] gap-4">
        {/* Results */}
        <div className="space-y-4 min-w-0 pt-4 sm:pt-0">
          <SearchList2
            configInfo={configInfo}
            checkin={checkin}
            checkout={checkout}
            deviceType={deviceType}
            searchAlternate={null}
            currentAirport={currentAirport}
            isCity={false}
            setHoverIndex={searchStore.setHoverIndex.bind(searchStore)}
            clearHoverIndex={searchStore.clearHoverIndex.bind(searchStore)}
          />
        </div>

        {/* Map (sticky) */}
        <aside className="hidden xl:block">
          <div className="sticky top-1 h-[calc(100vh-10px)] overflow-hidden rounded-2xl">
            {deviceType === 'desktop' && (
              <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                <SearchMap
                  markers={searchStore.searchResult.result}
                  currentAirport={currentAirport}
                  selectedFacilities={searchStore.selectedFacilities}
                  triggerIndex={(index) => {
                    searchStore.toogleIndex(index);
                  }}
                  hoverIndex={searchStore.hoverIndex}
                // handleMarkerOnClick={handleMarkerOnClick}
                // handleMouseEnter={handleMouseEnter}
                // deviceType={deviceType}
                />
              </LoadScriptNext>
            )}
          </div>
        </aside>

        <div className="xl:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-30">
          <Button
            type="primary"
            className={`flex flex-row justify-center items-center rounded-full h-12 px-6 bg-[#171C26] text-white shadow-pop font-bold gap-2 text-base border-none`}
            onClick={showModal}
          >
            <FontAwesomeIcon
              icon={faMap}
              {...props}
              style={{ width: 16 }}
            /> View map
          </Button>
        </div>


        {deviceType === 'mobile' && (
          <Modal
            title={
              <div className="flex flex-row justify-between items-center">
                {searchStore.selectedFacilities.length > 0 ? (
                  <>
                    <Button
                      className="text-primary text-base border border-primary rounded-full font-medium px-2"
                      onClick={() => searchStore.resetFacilities()}
                      onTouchStart={() => searchStore.resetFacilities()}
                    >
                      Reset
                    </Button>
                  </>
                ) : (
                  <p className="text-gray-400 text-base border-none px-2">
                    Reset
                  </p>
                )}
                <p className="">Select area</p>
                <Button
                  className="text-primary w-7 h-7 flex justify-center text-sm font-bold border border-primary rounded-full"
                  onClick={handleCancel}
                >
                  X
                </Button>
              </div>
            }
            closable={false}
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            // centered
            className="search-modal absolute h-full m-0 top-0 bottom-0"
            footer={
              <>
                <Button
                  type="primary"
                  className="w-full h-10 text-base font-bold rounded-full mt-4"
                  onClick={handleCancel}
                >
                  Back To Search Results
                </Button>
              </>
            }
          >
            <div className="lg:hidden w-full h-full flex flex-col">
              <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                <SearchMap
                  markers={searchStore.searchResult.result}
                  currentAirport={currentAirport}
                  selectedFacilities={searchStore.selectedFacilities}
                  triggerIndex={(index) => {
                    searchStore.toogleIndex(index);
                  }}
                  searchStore={searchStore}
                // handleMarkerOnClick={handleMarkerOnClick}
                // handleMouseEnter={handleMouseEnter}
                // hoverIndex={hoverIndex}
                // deviceType={deviceType}
                />
              </LoadScriptNext>
              {searchStore.selectedFacilities.length > 0 && (
                <span className="text-base text-center mt-2">
                  {searchStore.selectedFacilities.length} selected{' '}
                  {searchStore.selectedFacilities.length === 1
                    ? 'area'
                    : 'areas'}{' '}
                  to filter your search
                </span>
              )}
            </div>
          </Modal>
        )}
      </div>
      {searchStore.filteredFacilities?.length > 0 && (
        <NeedMoreData airport={currentAirport} />
      )}
    </Skeleton>
  );
}

export default observer(CardListC);
